import {
  type Error,
  type Validator,
  validateDNI,
  validateNIE,
  validateNIF
} from '@ps/web-shared-validators';
import { memoize, values } from 'lodash';
import type { Required } from 'utility-types';

import { request as apiRequest } from '@swf/core/api/request';
import type { RequestOptions } from '@swf/core/types/Request';

import {
  TXTCLI_DNI,
  TXTMOB_The_DNI_is_already_registered__ELL,
  TXTMOB_The_NIE_is_already_registered__ELL,
  TXTWCL_CPR_number,
  TXTWCL_Document_number,
  TXTWCL_Fiscal_Code_XNIFX,
  TXTWCL_NIE,
  TXTWCL_Please_check_that_your_CPR_num_ELL,
  TXTWCL_The_Fiscal_Code_you_have_provi_ELL
} from './taxCode.messages.json';

export enum TaxCodeType {
  DNI = 'DNI',
  NIE = 'NIE',
  NONE = 'NONE',
  NIF = 'NIF',
  CPR = 'CPR'
}

const TAX_CODE_LENGTH = 9;

const validateFormat = (taxCode: TaxCodeType, value: string): Error => {
  if (taxCode === TaxCodeType.DNI) {
    return validateDNI()(value);
  } else if (taxCode === TaxCodeType.NIE) {
    return validateNIE()(value);
  } else if (taxCode === TaxCodeType.NIF) {
    return validateNIF()(value);
  } else {
    return { valid: true };
  }
};

export const checkTaxCode = (
  options: Required<Partial<RequestOptions>, 'meta'>,
  message: Error['message'],
  taxCode: TaxCodeType
) => {
  const isUnique: Validator = memoize(
    async (value: string): Promise<Error> => {
      const { valid: isValidFormat } = validateFormat(taxCode, value);
      const isValidLength =
        value && (taxCode !== TaxCodeType.NONE ? value.length >= TAX_CODE_LENGTH : true);
      if (isValidLength && isValidFormat) {
        const {
          body: { unique }
        } = await apiRequest({
          ...options,
          service: 'wc3.user.check.fiscalCode',
          body: { fiscalCode: value }
        });

        return unique ? { valid: true } : { valid: false, message };
      }

      return { valid: false, message };
    },
    (...args) => values(args).join('_')
  );
  return isUnique as Validator;
};

export const uniqueValidationMessage = {
  [TaxCodeType.DNI]: TXTMOB_The_DNI_is_already_registered__ELL,
  [TaxCodeType.NIE]: TXTMOB_The_NIE_is_already_registered__ELL,
  [TaxCodeType.NIF]: TXTWCL_The_Fiscal_Code_you_have_provi_ELL,
  [TaxCodeType.CPR]: TXTWCL_Please_check_that_your_CPR_num_ELL,
  [TaxCodeType.NONE]: TXTWCL_The_Fiscal_Code_you_have_provi_ELL
};

export const taxLabel = {
  [TaxCodeType.DNI]: TXTCLI_DNI,
  [TaxCodeType.CPR]: TXTWCL_CPR_number,
  [TaxCodeType.NIE]: TXTWCL_NIE,
  [TaxCodeType.NIF]: TXTWCL_Fiscal_Code_XNIFX,
  [TaxCodeType.NONE]: TXTWCL_Document_number
};
